import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { CreditNoteCharge, CreateCreditNoteRequest, CreateCreditNoteResponse } from '@/api/billing/credit-note-schemas'
import {
  createCreditNote,
  getAssetDetails,
  getAssetVat,
  approveCreditNoteById,
  regenerateCreditNote,
  recreateCreditNote,
  cancelCreditNoteById,
  sendCreditNoteById,
} from '@/api/billing/credit-notes'
import {
  getCreditNoteStable,
  createCreditNoteLines,
  updateCreditNoteLines,
  deleteCreditNoteItemCreated,
  approveCreditNote,
  sendCreditNote,
  cancelCreditNote
} from '@/features/credit-notes/store'
import { InvoiceStatus } from '@/api/billing/constants'
import { updateInvoiceInListedInvoiceQueryCache } from './invoices'

export function useCreditNoteDraftSeed(assetCode: MaybeRefOrGetter<string | null | undefined>) {
  const enabled = computed(() => {
    const code = toValue(assetCode)
    return !!code && code.trim().length > 0
  })

  const assetQuery = useQuery({
    queryKey: computed(() => ['asset-details', toValue(assetCode)]),
    enabled,
    queryFn: () => getAssetDetails(toValue(assetCode)!)
  })

  const vatQuery = useQuery({
    queryKey: computed(() => ['asset-vat', toValue(assetCode)]),
    enabled,
    queryFn: () => getAssetVat(toValue(assetCode)!)
  })

  const vatPercentage = computed(() => vatQuery.data.value?.ratePercentage ?? 0)

  return {
    assetQuery,
    vatQuery,
    vatPercentage,
    isLoading: computed(() => assetQuery.isFetching.value || vatQuery.isFetching.value),
    isError: computed(() => assetQuery.isError.value || vatQuery.isError.value),
  }
}

export function useGetCreditNote(id: MaybeRefOrGetter<number>) {
  const noteId = computed(() => {
    const v = Number(toValue(id))
    return Number.isFinite(v) ? v : 0
  })

  const enabled = computed(() => noteId.value > 0)

  return useQuery({
    queryKey: computed(() => ['credit-notes', noteId.value]),
    enabled,
    queryFn: () => getCreditNoteStable(noteId.value),
    placeholderData: prev => prev
  })
}

export function useCreateCreditNoteLines(id: MaybeRefOrGetter<number>) {
  const qc = useQueryClient()

  const noteId = computed(() => {
    const v = Number(toValue(id))
    return Number.isFinite(v) ? v : 0
  })

  return useMutation({
    mutationFn: (payload: {
      charges: CreditNoteCharge[]
      vatPercentage: number
      notes?: string
    }) => {
      if (noteId.value <= 0) throw new Error('Invalid credit note id')
      return createCreditNoteLines(noteId.value, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', noteId.value] })
      qc.invalidateQueries({ queryKey: ['credit-notes'] })
    }
  })
}

export function useUpdateCreditNoteLines(id: MaybeRefOrGetter<number>) {
  const qc = useQueryClient()

  const noteId = computed(() => {
    const v = Number(toValue(id))
    return Number.isFinite(v) ? v : 0
  })

  return useMutation({
    mutationFn: (payload: {
      charges: CreditNoteCharge[]
      vatPercentage: number
      notes?: string
    }) => {
      if (noteId.value <= 0) throw new Error('Invalid credit note id')
      return updateCreditNoteLines(noteId.value, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', noteId.value] })
      qc.invalidateQueries({ queryKey: ['credit-notes'] })
    }
  })
}

export function useDeleteCreditNoteLine(id: MaybeRefOrGetter<number>) {
  const qc = useQueryClient()

  const noteId = computed(() => {
    const v = Number(toValue(id))
    return Number.isFinite(v) ? v : 0
  })

  return useMutation({
    mutationFn: (lineId: number) => {
      if (noteId.value <= 0) throw new Error('Invalid credit note id')
      return deleteCreditNoteItemCreated(noteId.value, lineId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-notes', noteId.value] })
      qc.invalidateQueries({ queryKey: ['credit-notes'] })
    }
  })
}

export function useApproveCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (draftId: number) => approveCreditNote(draftId),
    onSuccess: (_, draftId) => {
      qc.invalidateQueries({ queryKey: ['credit-notes'] })
      qc.invalidateQueries({ queryKey: ['credit-notes', draftId] })
    }
  })
}

export function useSendCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => sendCreditNoteById(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

export function useCancelCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cancelCreditNoteById(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

export function useCreateCreditNote() {
  return useMutation<CreateCreditNoteResponse, unknown, CreateCreditNoteRequest>({
    mutationFn: payload => createCreditNote(payload)
  })
}

export function useApproveCreditNoteFromDashboard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => approveCreditNoteById(toValue(creditNoteId)),
    async onSuccess(data, variables) {
      updateInvoiceInListedInvoiceQueryCache(
        queryClient,
        variables,
        cn => ({ ...cn, status: InvoiceStatus.Approved })
      )

        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: ['invoices', variables] }),
          queryClient.invalidateQueries({ queryKey: ['credit-notes', variables] }),
          queryClient.invalidateQueries({ queryKey: ['credit-notes:activity', variables] }),
        ])
    }
  })
}

export function useRegenerateCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => {
      return regenerateCreditNote(toValue(creditNoteId))
    }, onSuccess() {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

export function useReCreateCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => {
      return recreateCreditNote(toValue(creditNoteId))
    }, onSuccess() {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}